import { Button } from '@/components/ui'
import { formatBytes } from '@/lib/format'
import type { PendingCapture } from './offlineQueue'

/**
 * The offline queue, made visible — REQUIREMENTS.md §6.2.
 *
 * "Show a clear pending indicator" is a literal requirement, and for good
 * reason: a photo that exists only in this browser's IndexedDB is the one
 * copy. If Matt believes it uploaded and clears his phone, it's gone. So this
 * says plainly that the captures are still on the phone, and that they need
 * the app open to finish.
 *
 * That last part is the honest consequence of §6.2's own decision to skip
 * service-worker background sync. Nothing uploads while the phone is in a
 * pocket.
 */
export function PendingCaptures({
  pending,
  online,
  draining,
  onRetry,
}: {
  pending: readonly PendingCapture[]
  online: boolean
  draining: boolean
  onRetry: () => void
}) {
  if (pending.length === 0) {
    return online ? null : (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        You're offline. New photos will be saved on this device and uploaded
        when you're back on a connection.
      </p>
    )
  }

  const stuck = pending.filter((entry) => entry.attempts >= 3)

  return (
    <section
      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-amber-900">
            {pending.length} {pending.length === 1 ? 'photo' : 'photos'} waiting
            to upload
            {draining && ' — uploading now…'}
          </p>
          <p className="mt-0.5 text-xs text-amber-800">
            {online
              ? 'Kept on this device until the upload finishes. Leave the app open.'
              : "You're offline. These stay on this device until you reconnect."}
          </p>
        </div>

        {online && !draining && (
          <Button onClick={onRetry}>Try now</Button>
        )}
      </div>

      <ul className="mt-3 space-y-1 text-xs text-amber-900">
        {pending.map((entry) => (
          <li key={entry.id} className="flex justify-between gap-3">
            <span className="min-w-0 truncate">{entry.fileName}</span>
            <span className="shrink-0 text-amber-700">
              {formatBytes(entry.sizeBytes)}
              {entry.attempts > 0 && ` · ${entry.attempts} failed`}
            </span>
          </li>
        ))}
      </ul>

      {stuck.length > 0 && (
        <p className="mt-3 border-t border-amber-200 pt-2 text-xs text-amber-900">
          {stuck.length === 1 ? 'One photo has' : `${stuck.length} photos have`}{' '}
          failed repeatedly: {stuck[0]?.lastError ?? 'unknown error'}. The
          {stuck.length === 1 ? ' file is' : ' files are'} still here — try
          again on a stronger connection.
        </p>
      )}
    </section>
  )
}
